import React from 'react';
import { devLog } from '../utils/devLogger';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useUserAuth } from '../context/UserAuthContext';
import { useLang } from '../context/LangContext';
import ImageView from '../components/ImageView';
import PrimaryButton from '../components/PrimaryButton';
import FavoriteIcon from '../components/common/FavoriteIcon';
import AuctionHouseComponent from '../components/common/AuctionHouse';
import { LikedLotData } from '../lib/api/user/userApi.types';
import { isShoppingMode } from '../config/appMode';

// Styled Components
const LotCardContainer = styled.div`
  background: white;
  border-radius: 8px;
  overflow: visible;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  cursor: pointer;
  width: 100%;
  position: relative;

  &:hover {
    transform: translateY(-4px);
  }

  @media (min-width: 768px) {
    display: flex;
    flex-direction: column;
    height: auto;
  }
`;

const CardTopInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 12px;
  gap: 12px;
`;

const LiveEndTime = styled.div`
  font-size: 12px;
  color: #999;
  font-weight: 500;
  text-align: right;
  flex-shrink: 0;
`;

const CardMainContent = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  gap: 16px;
  align-items: stretch;

  @media (min-width: 768px) {
    gap: 24px;
  }
`;

const CardImageContainer = styled.div`
  flex: 1;
  aspect-ratio: 1 / 1;
  position: relative;
  overflow: visible;
  border-radius: 8px 0 0 8px;
  padding: 16px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CardContent = styled.div`
  padding: 8px 0px;
  text-align: left;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 1;
  margin-bottom: 8px;

  @media (min-width: 768px) {
    padding: 16px;
    margin-bottom: 12px;
  }
`;

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const AuctionHouseContainer = styled.div`
  width: 100%;
`;

const AuctionTitle = styled.div`
  font-size: 16px;
  color: #000;
  font-weight: 600;
  margin-bottom: 4px;
  text-align: left;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.3;

  @media (min-width: 768px) {
    font-size: 18px;
    line-height: 1.4;
  }
`;

const LotName = styled.h3`
  font-size: 14px;
  color: #000;
  margin: 0;
  font-weight: 400;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-align: left;

  @media (min-width: 768px) {
    font-size: 16px;
  }
`;

const PriceInfo = styled.div`
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;

  @media (min-width: 768px) {
    margin-top: 12px;
    gap: 8px;
  }
`;

const PriceItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PriceLabel = styled.span`
  font-size: 12px;
  color: #999;
  font-weight: 400;

  @media (min-width: 768px) {
    font-size: 13px;
  }
`;

const PriceValue = styled.span`
  font-size: 14px;
  color: #000;
  font-weight: 600;

  @media (min-width: 768px) {
    font-size: 15px;
  }
`;

const ButtonContent = styled.div`
  width: 100%;
  margin-top: 12px;

  @media (min-width: 768px) {
    margin-top: 16px;
  }
`;

const FavoriteIconContainer = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 32px;
  height: 32px;
  cursor: pointer;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (min-width: 768px) {
    top: 12px;
    right: 12px;
    width: 36px;
    height: 36px;
  }
`;

// Interfaces
interface ShoppingCardProps {
  item: LikedLotData;
  texts: {
    purchase: string;
    startPrice: string;
  };
  exchangeRate: number;
  formatPrice: (price: number, language: 'ko' | 'en', exchangeRate: number) => string;
  formatLiveEndTime?: (dateStr: string, language: 'ko' | 'en') => string;
  onCardClick: (item: LikedLotData) => void;
  onFavoriteToggle?: (e: React.MouseEvent, index: number) => Promise<void>;
  showFavoriteIcon?: boolean;
}

// Utility Functions
const getImageSrc = (images: string[]) => {
  if (!images || images.length === 0) {
    return '/common/noimage.svg';
  }
  
  const firstImage = images[0];
  
  if (firstImage.startsWith('http://') || firstImage.startsWith('https://')) {
    return firstImage;
  }
  
  const apiHost = process.env.REACT_APP_API_HOST || 'http://localhost:3001';
  return `${apiHost}/${firstImage}`;
};

const ShoppingCard: React.FC<ShoppingCardProps> = ({
  item,
  texts,
  exchangeRate,
  formatPrice,
  formatLiveEndTime,
  onCardClick,
  onFavoriteToggle,
  showFavoriteIcon = true
}) => {
  const navigate = useNavigate();
  const { user } = useUserAuth();
  const { lang } = useLang();

  const imageSrc = getImageSrc(item.images);
  
  const isLikedByUser = user?.index 
    ? item.likedUserIndexes.includes(user.index)
    : false;

  const handleCardClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('.favorite-icon-container') ||
        (e.target as HTMLElement).closest('button')) {
      return;
    }
    onCardClick(item);
  };

  const handleFavoriteClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onFavoriteToggle) {
      await onFavoriteToggle(e, item.index);
    }
  };

  const handlePurchaseClick = () => {
    devLog('🛒 [ShoppingCard] 구매하기 클릭:', { item, lotId: item.index });
    navigate(`/shopping-request/${item.index}`);
  };

  // 라이브 종료 시간 포맷팅
  const formattedLiveEndTime = formatLiveEndTime && item.auction?.auctionEndAt
    ? formatLiveEndTime(item.auction.auctionEndAt, lang as 'ko' | 'en')
    : '';

  return (
    <LotCardContainer onClick={handleCardClick}>
      {/* 상단 정보: LOT 이름과 라이브 종료 시간 */}
      <CardTopInfo>
        <LotName>{item.name}</LotName>
        {formattedLiveEndTime && !isShoppingMode() && <LiveEndTime>{formattedLiveEndTime}</LiveEndTime>}
      </CardTopInfo>

      {/* 메인 콘텐츠: 이미지와 텍스트 */}
      <CardMainContent>
        {/* 이미지 */}
        <CardImageContainer>
          <ImageView
            imageUrl={imageSrc}
            width="100%"
            height="100%"
            borderRadius={0}
            alt={item.name}
            objectFit="contain"
          />
          
          {/* 즐겨찾기 아이콘 */}
          {showFavoriteIcon && (
            <FavoriteIconContainer 
              className="favorite-icon-container"
            >
              <FavoriteIcon
                isFavorite={isLikedByUser}
                onClick={handleFavoriteClick}
              />
            </FavoriteIconContainer>
          )}
        </CardImageContainer>

        {/* 텍스트 콘텐츠 */}
        <CardContent>
          <TextContent>
            {/* 경매사 정보 */}
            <AuctionHouseContainer>
              <AuctionHouseComponent
                auctioneerName={item.auctioneer?.name || ''}
                auctioneerIndex={item.auctioneer?.index || 0}
              />
            </AuctionHouseContainer>

            {/* 경매 타이틀 */}
            <AuctionTitle>{item.auction?.name || ''}</AuctionTitle>

            {/* 가격 정보 */}
            <PriceInfo>
              <PriceItem>
                <PriceLabel>{texts.startPrice}</PriceLabel>
                <PriceValue>
                  {formatPrice(item.startPrice, lang as 'ko' | 'en', exchangeRate)}
                </PriceValue>
              </PriceItem>
            </PriceInfo>
          </TextContent>

          {/* 구매하기 버튼 (항상 표시) */}
          <ButtonContent>
            <PrimaryButton onClick={handlePurchaseClick}>
              {texts.purchase}
            </PrimaryButton>
          </ButtonContent>
        </CardContent>
      </CardMainContent>
    </LotCardContainer>
  );
};

export default ShoppingCard;
